import { BOARD_SIZE, COLUMN_SIZE } from './constants';
import { BoardOf, CellValue, ColumnOf, ColumnsOf, Cracked, DotValue, Solid } from './types';

export const sleep = async (ms: number): Promise<void> =>
  new Promise((fulfill) => {
    setTimeout(fulfill, ms);
  });

export const formatWithCommas = (value: number): string => {
  let str = value.toString();
  if (value < 1000) return str;
  const parts: string[] = [];
  while (str.length > 0) {
    parts.push(str.slice(-3));
    str = str.slice(0, -3);
  }
  return parts.reverse().join(',');
};

/**
 * Get the score multiplier for the current combo.
 * Returns 0 if combo <= 0.
 *
 * https://www.reddit.com/r/drop7/comments/b3dg1w/drop7_scoring
 * https://gaming.stackexchange.com/questions/18827/what-is-the-sequence-in-drop7
 */
export const getComboMultiplier = (combo: number): number => {
  if (combo <= 0) return 0;
  return Math.floor(7 * (combo ** 2.5));
};

export const toColumns = <T>(cells: BoardOf<T>): ColumnsOf<T> => {
  const columns = [];
  for (let column = 0; column < COLUMN_SIZE; column++) {
    const col = [];
    for (let i = column; i < BOARD_SIZE; i += COLUMN_SIZE) {
      col.push(cells[i]);
    }
    columns.push(col as ColumnOf<T>);
  }
  return columns as ColumnsOf<T>;
};

export const makeColumnsOf = <T>(value: T): ColumnsOf<T> => {
  const columns = [];
  for (let column = 0; column < COLUMN_SIZE; column++) {
    columns.push(new Array(COLUMN_SIZE).fill(value));
  }
  return columns as ColumnsOf<T>;
};

export const toBoard = <T>(columns: ColumnsOf<T>): BoardOf<T> => {
  const board = new Array(BOARD_SIZE).fill(null);
  for (let row = 0; row < COLUMN_SIZE; row++) {
    for (let column = 0; column < COLUMN_SIZE; column++) {
      const index = (row * COLUMN_SIZE) + column;
      board[index] = columns[column][row];
    }
  }
  return board as BoardOf<T>;
};

export const solid = (value: DotValue): Solid => value + 7 as Solid;

export const isDotValue = (value: CellValue): value is DotValue =>
  value >= 1 && value <= 7;

export const isSolid = (value: CellValue): value is Solid =>
  value >= 8 && value <= 14;

export const isCracked = (value: CellValue): value is Cracked =>
  value >= 15 && value <= 21;

export const isSolidOrCracked = (value: CellValue): value is Solid | Cracked =>
  value >= 8 && value <= 21;

/**
 * Updates a pre-existing average value to include the current value.
 * @param value - The new value to include in the average
 * @param current - The pre-existing average
 * @param count - The number of values in `current`
 * @returns The new average value. Note that its count is `count + 1`
 */
export const updateAverage = (value: number, current: number = 0, count: number = 0): number => {
  if (count === 0) return value;
  // average = sum / count
  // -> sum = average * count
  // new average = (sum + value) / (count + 1)
  // -> new average = ((average * count) + value) / (count + 1)
  return ((current * count) + value) / (count + 1);
};
